import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { getAllReviews, deleteReview, approveReview } from '../../lib/firebaseService';
import { Star, Trash2, Check, X, MessageSquare } from 'lucide-react';

export default function AdminReviews() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    loadReviews();
  }, [user, navigate]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const data = await getAllReviews();
      console.log('Admin loaded reviews:', data);
      setReviews(data);
    } catch (error) {
      console.error('Xəta:', error);
      toast.error('Rəylər yüklənə bilmədi');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu rəyi silmək istədiyinizə əminsiniz?')) return;

    try {
      await deleteReview(id);
      toast.success('Rəy silindi');
      loadReviews();
    } catch (error) {
      console.error('Xəta:', error);
      toast.error('Rəy silinə bilmədi');
    }
  };

  const handleApprove = async (id) => {
    try {
      console.log('Approving review with ID:', id);
      await approveReview(id);
      toast.success('Rəy təsdiqləndi! Ana səhifəni yeniləyin.');
      // Force reload to update UI
      await loadReviews();
      console.log('Reviews reloaded after approval');
    } catch (error) {
      console.error('Approve error:', error);
      toast.error('Rəy təsdiqlənə bilmədi: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Yüklənir...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-primary">Admin Panel</h1>
          </div>
          <div className="flex gap-3">
            <Link to="/admin">
              <Button variant="outline">Admin Dashboard</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <MessageSquare className="w-8 h-8" />
            Müştəri Rəyləri
          </h1>
          <p className="text-muted-foreground mt-1">
            Rəyləri idarə edin və təsdiqləyin
          </p>
        </div>

      {reviews.length === 0 ? (
        <div className="bg-white rounded-xl border border-border p-12 text-center">
          <MessageSquare className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Heç bir rəy yoxdur</p>
        </div>
      ) : (
        <div className="grid gap-6">
          {reviews.map((review) => (
            <div
              key={review.id}
              className={`bg-white rounded-xl border-2 p-6 transition-all ${
                review.approved ? 'border-green-200 bg-green-50' : 'border-border'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-bold text-lg">{review.name}</h3>
                    {review.approved && (
                      <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Təsdiqləndi
                      </span>
                    )}
                    {!review.approved && (
                      <span className="bg-yellow-600 text-white text-xs px-2 py-1 rounded-full">
                        Gözləyir
                      </span>
                    )}
                  </div>
                  {review.business && (
                    <p className="text-sm text-muted-foreground mb-2">{review.business}</p>
                  )}
                  {review.email && (
                    <p className="text-xs text-muted-foreground">{review.email}</p>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < review.rating
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>

              <p className="text-gray-700 mb-4 leading-relaxed">"{review.text}"</p>

              <div className="flex items-center gap-2 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground flex-1">
                  {review.created_at?.toDate?.().toLocaleDateString('az-AZ', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>

                {!review.approved && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-600 border-green-600 hover:bg-green-50"
                    onClick={() => handleApprove(review.id)}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Təsdiqlə
                  </Button>
                )}

                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-600 hover:bg-red-50"
                  onClick={() => handleDelete(review.id)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Sil
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Qeyd:</strong> Təsdiqlənmiş rəylər ana səhifədə göstərilir. Rəyləri yoxlayıb təsdiqləyin və ya silin.
          </p>
        </div>
      </main>
    </div>
  );
}
